import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useSendTicketReply } from '../../hooks/queries/useTickets';
import { renderReplyTemplate } from '../../services/tickets/replyTemplates';
import type { ReplyTemplateKey } from '../../services/tickets/replyTemplates';
import type { TicketWithRelations } from '../../services/tickets/ticketService';

interface TicketReplyModalProps {
  ticket: TicketWithRelations;
  templateKey: ReplyTemplateKey;
  /** Nom de l'avocat connecté — injecté dans la signature du modèle. */
  lawyerName: string;
  /** Email de l'avocat connecté — affiché en "Réponse à". */
  lawyerEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TicketReplyModal({
  ticket,
  templateKey,
  lawyerName,
  lawyerEmail,
  open,
  onOpenChange,
}: TicketReplyModalProps) {
  const sendReply = useSendTicketReply();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // Re-remplit le formulaire à chaque (ré)ouverture ou changement de modèle.
  useEffect(() => {
    if (!open) return;
    const rendered = renderReplyTemplate(templateKey, ticket.sender_name, lawyerName);
    setSubject(rendered.subject);
    setBody(rendered.body);
  }, [open, templateKey, ticket.sender_name, lawyerName]);

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && !sendReply.isPending;

  function handleSend() {
    if (!canSend) return;
    sendReply.mutate(
      {
        ticketId: ticket.id,
        templateKey,
        subject: subject.trim(),
        body: body.trim(),
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40 animate-in fade-in" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl bg-cime-surface rounded-cime shadow-xl border border-neutral-200 outline-none"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
            <Dialog.Title className="font-heading text-lg font-semibold text-cime-text-primary">
              Répondre à {ticket.sender_name ?? 'ce candidat'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-neutral-400 hover:text-neutral-600" aria-label="Fermer">
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* En-têtes figés */}
          <div className="px-5 pt-4 space-y-1.5 text-sm">
            <div className="flex gap-2">
              <span className="w-24 shrink-0 text-neutral-400">À</span>
              <span className="text-neutral-700">{ticket.sender_email}</span>
            </div>
            <div className="flex gap-2">
              <span className="w-24 shrink-0 text-neutral-400">Réponse à</span>
              <span className="text-neutral-700">{lawyerEmail}</span>
            </div>
          </div>

          {/* Objet */}
          <div className="px-5 pt-4">
            <label className="block text-xs font-medium text-neutral-500 mb-1">Objet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          {/* Message */}
          <div className="px-5 pt-3 pb-4">
            <label className="block text-xs font-medium text-neutral-500 mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="w-full rounded-cime border border-neutral-200 px-3 py-2 text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-cime-primary/30 resize-y"
            />
            <p className="mt-1.5 text-xs text-neutral-400">
              Ce message sera envoyé au candidat. Relisez-le avant l'envoi.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-neutral-200">
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm">
                Annuler
              </Button>
            </Dialog.Close>
            <Button size="sm" onClick={handleSend} disabled={!canSend} isLoading={sendReply.isPending}>
              Envoyer
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
